import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { alpha, type Theme } from '@mui/material/styles';

interface CopyableTextProps {
  text: string;
  fontWeight?: number;
  fontSize?: string;
}

export default function CopyableText({
  text,
  fontWeight = 500,
  fontSize = '0.8125rem',
}: CopyableTextProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Don't trigger row click
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {
          console.warn('[CopyableText] Clipboard API unavailable');
        });
    },
    [text]
  );

  return (
    <Tooltip title={copied ? 'Copied!' : 'Click to copy'} arrow placement="top">
      <Box
        onClick={handleCopy}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          borderRadius: 0.5,
          px: 0.5,
          mx: -0.5,
          '&:hover': {
            bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
          },
          '&:hover .copy-icon': {
            opacity: 1,
          },
        }}
      >
        <Typography sx={{ fontWeight, fontSize }}>{text}</Typography>
        <ContentCopyIcon
          className="copy-icon"
          sx={{
            fontSize: '0.75rem',
            opacity: 0,
            color: 'text.secondary',
            transition: 'opacity 0.15s',
          }}
        />
      </Box>
    </Tooltip>
  );
}
